import React, { useEffect, useState } from "react";
import Modal from "../../../../components/ModalComponents/Modal";
import { createMarketingList, fetchMarketingList } from "../../../../api/candidiate";
import Loader from "../../../../components/Loader";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { formatDate, formatDateCST, formatTimeCST, getUserInfoDetails, mapToOption } from "../../../../utlis";

import { formatPhoneNumber } from "../../../Sales/SalesTeams/components/utils";
import useFetch from "../../../../hooks/useFetch";
import MyTable from "../../../../components/MyTable";
import { RxReload } from 'react-icons/rx';
const { companyId, clientId } = getUserInfoDetails();

const MarketingList = () => {
  const initialMarketingList = {
    salesManager: "",
    salesManagerName: "",
    salesPerson: "",
    salesStartDate: "",
    salesEndDate: "",
    jobsApplied: "",
    interviewsAttended: "",
    marketingStatus: "",
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const candidateId = useSelector(state => state.personal.candidateId);

  const [marketingListData, setMarketingListData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [marketingList, setMarketingList] = useState(initialMarketingList);

  // Sales Person View Modal state
  const [isSalesViewOpen, setIsSalesViewOpen] = useState(false);
  const [salesViewData, setSalesViewData] = useState(null);
  const [salesViewLoading, setSalesViewLoading] = useState(false);

  const { data: refSalesPerson, loading: refSalesLoading } = useFetch('/salesteam', {
    company: companyId, client: clientId, userType: 'sales', status: 'active'
  });

  const salesPersonRecords = refSalesPerson?.data?.records || [];

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMarketingList((prev) => ({ ...prev, [name]: value }));
  };

  // When sales person is selected, auto-populate sales manager from the same record
  const handleSalesPersonChange = (e) => {
    const selectedId = e.target.value;
    setMarketingList((prev) => {
      if (!selectedId) {
        return { ...prev, salesPerson: "", salesManager: "", salesManagerName: "" };
      }
      const person = salesPersonRecords.find((r) => String(r.sales_person_id) === selectedId);
      return {
        ...prev,
        salesPerson: selectedId,
        salesManager: person?.manager_id ? String(person.manager_id) : "",
        salesManagerName: person?.manager_name || "",
      };
    });
  };

  // View a sales person's details in a read-only popup
  const handleViewSalesPerson = (salesPersonId) => {
    if (!salesPersonId) {
      toast.error('Sales person ID not available');
      return;
    }
    setSalesViewLoading(true);
    setIsSalesViewOpen(true);
    try {
      const records = salesPersonRecords;
      const person = records.find((r) => String(r.sales_person_id) === String(salesPersonId));
      if (!person) {
        toast.error('Sales person not found');
        setIsSalesViewOpen(false);
        return;
      }
      setSalesViewData({
        firstName: person.first_name || '',
        middleName: person.middle_name || '',
        lastName: person.last_name || '',
        preferredName: person.preferred_name || '',
        mobileNum: formatPhoneNumber(person.mobile || ''),
        email: person.email || '',
        jobTitle: person.job_title || '',
        managerName: person.manager_name || '',
        contactName: person.contact_name || '',
        contactPhoneNum: formatPhoneNumber(person.contact_mobile || ''),
        contactEmail: person.contact_email || '',
        contactRelation: person.relation || '',
        notes: person.notes || '',
        salesStatus: person.status || '',
        created: person.created || '',
        updated: person.updated || '',
        created_by: person.created_by || '',
        updated_by: person.updated_by || '',
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch sales person details');
      setIsSalesViewOpen(false);
    } finally {
      setSalesViewLoading(false);
    }
  };

  const closeSalesView = () => {
    setIsSalesViewOpen(false);
    setSalesViewData(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await createMarketingList(marketingList, candidateId);
      toast.success('Marketing list added successfully');
      setMarketingList(initialMarketingList);
      closeModal();
      getMarketingListData();
    } catch (err) {
      console.error(err);
      const data = err?.response?.data;
      toast.error(data?.details || data?.message || 'Failed to save marketing list');
    } finally {
      setLoading(false);
    }
  };

  const getMarketingListData = async () => {
    if (!candidateId) return;
    setLoading(true);
    try {
      const res = await fetchMarketingList({ candidateId, pageNum: 1, pageSize: 100 });
      setMarketingListData(res.data?.response?.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch marketing list');
      setMarketingListData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (candidateId) {
      getMarketingListData();
    }
  }, [candidateId]);

  const hasActiveRecord = marketingListData.some((m) => m.status?.toLowerCase() === 'active');

  // Transform API data for MyTable
  const transformedData = marketingListData.map((m) => ({
    record_id: m.record_id,
    salesPersonId: m.sales_person_id || '',
    managerId: m.sales_manager_id || m.manager_id || '',
    salesPerson: m.sales_person_name || '-',
    salesManager: m.sales_manager_name || '-',
    startDate: m.start_date ? formatDate(m.start_date) : '-',
    endDate: m.end_date ? formatDate(m.end_date) : '-',
    jobsApplied: m.total_jobs_applied ?? '-',
    interviews: m.total_interviews_attended ?? '-',
    interviewsFailed: m.total_interviews_failed ?? '-',
    interviewsPassed: m.total_interviews_passed ?? '-',
    status: m.status || '-',
  }));

  // Filter data based on search term
  const filteredData = transformedData.filter((row) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      row.salesPerson.toLowerCase().includes(term) ||
      row.salesManager.toLowerCase().includes(term) ||
      row.startDate.toLowerCase().includes(term) ||
      row.endDate.toLowerCase().includes(term) ||
      String(row.jobsApplied).toLowerCase().includes(term) ||
      String(row.interviews).toLowerCase().includes(term) ||
      String(row.interviewsFailed).toLowerCase().includes(term) ||
      String(row.interviewsPassed).toLowerCase().includes(term) ||
      row.status.toLowerCase().includes(term)
    );
  });

  const columns = [
    {
      name: 'record_id',
      label: 'ID',
      options: { display: false },
    },
    {
      name: 'salesPersonId',
      label: 'SP ID',
      options: { display: false, filter: false, searchable: false },
    },
    {
      name: 'managerId',
      label: 'Mgr ID',
      options: { display: false, filter: false, searchable: false },
    },
    {
      name: 'salesPerson',
      label: 'Sales Person',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const spId = tableMeta.rowData[1];
          return value && value !== '-' ? (
            <p className="text-base font-bold text-auxiliary-800 cursor-pointer hover:underline" onClick={() => handleViewSalesPerson(spId)}>{value}</p>
          ) : (
            <p className="text-text-hint">{value}</p>
          );
        },
      },
    },
    {
      name: 'salesManager',
      label: 'Sales Manager',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const mgrId = tableMeta.rowData[2];
          return value && value !== '-' ? (
            <p className="text-base font-bold text-auxiliary-800 cursor-pointer hover:underline" onClick={() => handleViewSalesPerson(mgrId)}>{value}</p>
          ) : (
            <p className="text-text-hint">{value}</p>
          );
        },
      },
    },
    {
      name: 'startDate',
      label: 'Sales Start Date',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <p className="text-text-hint">{value}</p>
        ),
      },
    },
    {
      name: 'endDate',
      label: 'Sales End Date',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <p className="text-text-hint">{value}</p>
        ),
      },
    },
    {
      name: 'jobsApplied',
      label: 'Jobs Applied',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <p className="text-text-hint">{value}</p>
        ),
      },
    },
    {
      name: 'interviews',
      label: 'Interviews',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <p className="text-text-hint">{value}</p>
        ),
      },
    },
    {
      name: 'interviewsFailed',
      label: 'Failed',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <p className="text-text-hint">{value}</p>
        ),
      },
    },
    {
      name: 'interviewsPassed',
      label: 'Passed',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <p className="text-text-hint">{value}</p>
        ),
      },
    },
    {
      name: 'status',
      label: 'Marketing Status',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <p className="text-text-default font-bold">{value}</p>
        ),
      },
    },
  ];

  const tableOptions = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 15, 20, 50],
    selectableRows: 'none',
    elevation: 0,
    filter: true,
    search: false,
    download: true,
    print: true,
    viewColumns: true,
    textLabels: {
      body: {
        noMatch: !isLoading && marketingListData.length === 0 ? 'No marketing records found' : '',
      },
    },
  };

  return (
    <div>
      {isLoading && <Loader />}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <div className="relative">
          {/* Submitting overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-2"></div>
                <span className="text-secondary-700 font-medium">Saving...</span>
              </div>
            </div>
          )}

          {/* Modal Header with Close Button */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-secondary-800">
              Activate Marketing
            </h2>
            <button
              type="button"
              onClick={closeModal}
              className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <form className="flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-5">
              <div className="flex flex-col w-72">
                <label htmlFor="salesPerson" className="text-text-hint mb-1">
                  Sales Person
                </label>
                <select
                  onChange={handleSalesPersonChange}
                  name="salesPerson"
                  id="salesPerson"
                  value={marketingList.salesPerson}
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2"
                >
                  <option value="">Select Sales Person</option>
                  {!refSalesLoading && mapToOption(salesPersonRecords, 'first_name', 'sales_person_id').map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col w-72">
                <label htmlFor="salesManager" className="text-text-hint mb-1">
                  Sales Manager
                </label>
                <input
                  value={marketingList.salesManagerName}
                  disabled={true}
                  placeholder="Auto-populated from Sales Person"
                  id="salesManager"
                  className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div className="flex flex-col w-72">
                <label htmlFor="salesStartDate" className="text-text-hint mb-1">
                  Sales Start Date
                </label>
                <input
                  onChange={handleChange}
                  value={marketingList.salesStartDate}
                  name="salesStartDate"
                  type="date"
                  id="salesStartDate"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex flex-col w-72">
                <label htmlFor="salesEndDate" className="text-text-hint mb-1">
                  Sales End Date
                </label>
                <input
                  value={marketingList.salesEndDate}
                  disabled={true}
                  name="salesEndDate"
                  type="date"
                  id="salesEndDate"
                  className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div className="flex flex-col w-72">
                <label htmlFor="jobsApplied" className="text-text-hint mb-1">
                  Jobs Applied
                </label>
                <input
                  value={marketingList.jobsApplied || "0"}
                  disabled={true}
                  id="jobsApplied"
                  className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black"
                />
              </div>
              <div className="flex flex-col w-72">
                <label htmlFor="interviewsAttended" className="text-text-hint mb-1">
                  Interviews Attended
                </label>
                <input
                  value={marketingList.interviewsAttended || "0"}
                  disabled={true}
                  id="interviewsAttended"
                  className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black"
                />
              </div>
            </div>

            {/* Marketing Status */}
            <div>
              <div className="w-96">
                <label className="text-text-hint">Marketing Status</label>
                <ul className="grid grid-cols-3 mt-1">
                  {['Active', 'InActive', 'Suspended'].map((status) => (
                    <li className="relative" key={status}>
                      <input
                        onChange={handleChange}
                        type="radio"
                        value={status}
                        checked={marketingList.marketingStatus === status}
                        name="marketingStatus"
                        id={`mkt_status_${status}`}
                        className="sr-only peer"
                      />
                      <label
                        className={`flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 cursor-pointer focus:outline-none hover:bg-gray-50 ${
                          marketingList.marketingStatus === status ? '!bg-secondary-500 text-white border-secondary-800' : ''
                        } ${
                          status === 'Active'
                            ? 'rounded-tl-full rounded-bl-full'
                            : status === 'Suspended'
                            ? 'rounded-tr-full rounded-br-full'
                            : ''
                        }`}
                        htmlFor={`mkt_status_${status}`}
                      >
                        {status}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </form>

          {/* Buttons */}
          <div className="flex justify-between p-6 mt-6">
            <button
              type="button"
              onClick={closeModal}
              disabled={isLoading}
              className="border border-secondary-800 text-secondary-800 py-2 px-5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="bg-secondary-700 text-text-light py-2 px-5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Sales Person View Modal (Read-Only) */}
      <Modal isOpen={isSalesViewOpen} onClose={closeSalesView}>
        <div className="relative">
          {/* Loading overlay */}
          {salesViewLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-2"></div>
                <span className="text-secondary-700 font-medium">Loading...</span>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-secondary-800">
              Sales Person
              {salesViewData && (
                <span style={{ color: 'rgb(178, 120, 33)', marginLeft: 8 }}>
                  - {[salesViewData.firstName, salesViewData.middleName, salesViewData.lastName].filter(Boolean).join(' ')}
                </span>
              )}
            </h2>
            <button
              type="button"
              onClick={closeSalesView}
              className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          {salesViewData && (
            <div className="flex flex-col gap-6 text-text-hint">
              {/* Personal Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <div className="flex flex-col">
                  <label className="text-text-hint mb-1">First Name</label>
                  <input disabled value={salesViewData.firstName} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" />
                </div>
                <div className="flex flex-col">
                  <label className="text-text-hint mb-1">Middle Name</label>
                  <input disabled value={salesViewData.middleName} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" />
                </div>
                <div className="flex flex-col">
                  <label className="text-text-hint mb-1">Last Name</label>
                  <input disabled value={salesViewData.lastName} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <div className="flex flex-col">
                  <label className="text-text-hint mb-1">Preferred Name</label>
                  <input disabled value={salesViewData.preferredName} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" />
                </div>
                <div className="flex flex-col">
                  <label className="text-text-hint mb-1">Mobile Number</label>
                  <input disabled value={salesViewData.mobileNum} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" />
                </div>
                <div className="flex flex-col">
                  <label className="text-text-hint mb-1">Email</label>
                  <input disabled value={salesViewData.email} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" />
                </div>
                <div className="flex flex-col">
                  <label className="text-text-hint mb-1">Job Title</label>
                  <input disabled value={salesViewData.jobTitle} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" />
                </div>
                <div className="flex flex-col">
                  <label className="text-text-hint mb-1">Reporting Manager</label>
                  <input disabled value={salesViewData.managerName} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" />
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <div className="flex flex-col">
                  <label className="text-text-hint mb-1">Contact's Name</label>
                  <input disabled value={salesViewData.contactName} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" />
                </div>
                <div className="flex flex-col">
                  <label className="text-text-hint mb-1">Contact's Phone Number</label>
                  <input disabled value={salesViewData.contactPhoneNum} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" />
                </div>
                <div className="flex flex-col">
                  <label className="text-text-hint mb-1">Contact's Email</label>
                  <input disabled value={salesViewData.contactEmail} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" />
                </div>
                <div className="flex flex-col">
                  <label className="text-text-hint mb-1">Contact's Relation</label>
                  <input disabled value={salesViewData.contactRelation} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" />
                </div>
              </div>

              {/* Notes */}
              <div className="flex flex-col">
                <label className="text-text-hint mb-1">Notes</label>
                <textarea disabled value={salesViewData.notes} className="outline-none border-2 border-neutral-300 rounded-lg px-3 py-2 bg-gray-50 text-black" rows={4} />
              </div>

              {/* Status */}
              <div>
                <label className="text-text-hint mb-1 block">Status</label>
                <span className="inline-block px-4 py-2 border border-secondary-800 rounded-full bg-secondary-500 text-white font-bold capitalize">
                  {salesViewData.salesStatus}
                </span>
              </div>

              {/* Audit Footer */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-col space-y-1">
                  <label className="text-text-hint text-xs">Created By</label>
                  <p className="text-sm text-gray-600">{salesViewData.created_by || 'N/A'}</p>
                </div>
                <div className="flex flex-col space-y-1">
                  <label className="text-text-hint text-xs">Date Created</label>
                  <p className="text-sm text-gray-600">
                    {salesViewData.created ? formatDateCST(salesViewData.created) : 'N/A'}
                  </p>
                  {salesViewData.created && (
                    <p className="text-xs text-gray-500">{formatTimeCST(salesViewData.created)}</p>
                  )}
                </div>
                <div className="flex flex-col space-y-1">
                  <label className="text-text-hint text-xs">Updated By</label>
                  <p className="text-sm text-gray-600">{salesViewData.updated_by || 'N/A'}</p>
                </div>
                <div className="flex flex-col space-y-1">
                  <label className="text-text-hint text-xs">Last Updated</label>
                  <p className="text-sm text-gray-600">
                    {salesViewData.updated ? formatDateCST(salesViewData.updated) : 'N/A'}
                  </p>
                  {salesViewData.updated && (
                    <p className="text-xs text-gray-500">{formatTimeCST(salesViewData.updated)}</p>
                  )}
                </div>
              </div>

              {/* Close Button */}
              <div className="flex justify-center p-6">
                <button
                  type="button"
                  onClick={closeSalesView}
                  className="border border-secondary-800 text-secondary-800 py-2 px-5 rounded-full"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <div className="mt-5 rounded-lg">
        <div className="flex justify-between items-center gap-2 p-3">
          <div className="flex gap-2 items-center">
            <button
              onClick={getMarketingListData}
              className="p-2 hover:bg-gray-100 rounded cursor-pointer"
              title="Refresh"
              disabled={isLoading}
            >
              <RxReload size={20} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          <div className="flex-1 mx-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by sales person, manager, status..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none"
            />
          </div>
          <button
            onClick={openModal}
            disabled={hasActiveRecord}
            title={hasActiveRecord ? 'An active marketing record already exists' : ''}
            className={`py-2 px-6 rounded-full ${hasActiveRecord ? 'bg-neutral-200 text-gray-400 cursor-not-allowed' : 'bg-secondary-700 text-white hover:bg-secondary-800'}`}
          >
            Activate Marketing
          </button>
        </div>
        <MyTable
          title=""
          data={isLoading ? [] : filteredData}
          columns={columns}
          options={tableOptions}
        />
      </div>
    </div>
  );
};

export default MarketingList;
